import { Dispatch, Action, ActionCreator } from "redux";
import { ThunkAction } from "redux-thunk";

import { GlobalState } from "@/Reducers/interface";
import { BulkImportResponse } from "@/Contracts/BulkImport";
import { Opportunity } from "@/Contracts/Opportunity";
import { Product } from "@/Contracts/Products";
import { Client, ClientContact } from "@/Contracts/Clients";

import {
	opportunitiesBulkImport,
	productsBulkImport,
	clientsBulkImport,
	clientContactsBulkImport,
	assessmentIssuesBulkImport,
	qbrIssuesBulkImport
} from "@/Services/BulkImport";
import { AssessmentDataLog } from "@/Contracts/Assessments";
import { HealthStatusDataLog } from "@/Contracts/QBR";

export enum BulkImportActionTypes {
	SHOW_BULK_IMPORT_LOADER = "SHOW_BULK_IMPORT_LOADER",
	HIDE_BULK_IMPORT_LOADER = "HIDE_BULK_IMPORT_LOADER",
	OPPORTUNITIES_BULK_IMPORT_REQUESTED = "OPPORTUNITIES_BULK_IMPORT_REQUESTED",
	OPPORTUNITIES_BULK_IMPORT_SUCCESSFUL = "OPPORTUNITIES_BULK_IMPORT_SUCCESSFUL",
	OPPORTUNITIES_BULK_IMPORT_FAILED = "OPPORTUNITIES_BULK_IMPORT_FAILED",
	PRODUCTS_BULK_IMPORT_REQUESTED = "PRODUCTS_BULK_IMPORT_REQUESTED",
	PRODUCTS_BULK_IMPORT_SUCCESSFUL = "PRODUCTS_BULK_IMPORT_SUCCESSFUL",
	PRODUCTS_BULK_IMPORT_FAILED = "PRODUCTS_BULK_IMPORT_FAILED",
	CLIENTS_BULK_IMPORT_REQUESTED = "CLIENTS_BULK_IMPORT_REQUESTED",
	CLIENTS_BULK_IMPORT_SUCCESSFUL = "CLIENTS_BULK_IMPORT_SUCCESSFUL",
	CLIENTS_BULK_IMPORT_FAILED = "CLIENTS_BULK_IMPORT_FAILED",

	CLIENT_CONTACTS_BULK_IMPORT_REQUESTED = "CLIENT_CONTACTS_BULK_IMPORT_REQUESTED",
	CLIENT_CONTACTS_BULK_IMPORT_SUCCESSFUL = "CLIENT_CONTACTS_BULK_IMPORT_SUCCESSFUL",
	CLIENT_CONTACTS_BULK_IMPORT_FAILED = "CLIENT_CONTACTS_BULK_IMPORT_FAILED",
	ISSUES_BULK_IMPORT_REQUESTED = "ASSESSMENTS_ISSUES_BULK_IMPORT_REQUESTED",
	ISSUES_BULK_IMPORT_SUCCESSFUL = "ASSESSMENTS_ISSUES_BULK_IMPORT_SUCCESSFUL",
	ISSUES_BULK_IMPORT_FAILED = "ASSESSMENTS_ISSUES_BULK_IMPORT_FAILED",
	BULK_IMPORT_RESET = "BULK_IMPORT_RESET",
	SPREADSHEET_BULK_IMPORT_DRAWER_OPENED = "SPREADSHEET_BULK_IMPORT_DRAWER_OPENED",
	SPREADSHEET_BULK_IMPORT_DRAWER_CLOSED = "SPREADSHEET_BULK_IMPORT_DRAWER_CLOSED",
	SOFTWARE_BULK_IMPORT_DRAWER_OPENED = "SOFTWARE_BULK_IMPORT_DRAWER_OPENED",
	SOFTWARE_BULK_IMPORT_DRAWER_CLOSED = "SOFTWARE_BULK_IMPORT_DRAWER_CLOSED",
	EMAIL_BULK_IMPORT_DRAWER_OPENED = "EMAIL_BULK_IMPORT_DRAWER_OPENED",
	EMAIL_BULK_IMPORT_DRAWER_CLOSED = "EMAIL_BULK_IMPORT_DRAWER_CLOSED",
	BULK_IMPORT_ENTITY_LIST_DRAWER_OPENED = "BULK_IMPORT_ENTITY_LIST_DRAWER_OPENED",
	BULK_IMPORT_ENTITY_LIST_DRAWER_CLOSED = "BULK_IMPORT_ENTITY_LIST_DRAWER_CLOSED",
	RESTORE_BULK_IMPORT_STATE = "RESTORE_BULK_IMPORT_STATE"
}

export type ShowBulkImportLoaderAction =
	Action<BulkImportActionTypes.SHOW_BULK_IMPORT_LOADER>;
export type HideBulkImportLoaderAction =
	Action<BulkImportActionTypes.HIDE_BULK_IMPORT_LOADER>;
export type OpportunitiesBulkImportRequestedAction =
	Action<BulkImportActionTypes.OPPORTUNITIES_BULK_IMPORT_REQUESTED>;
export interface OpportunitiesBulkImportSuccessfulAction
	extends Action<BulkImportActionTypes.OPPORTUNITIES_BULK_IMPORT_SUCCESSFUL> {
	bulkImportData: BulkImportResponse<Opportunity>;
}
export type OpportunitiesBulkImportFailedAction =
	Action<BulkImportActionTypes.OPPORTUNITIES_BULK_IMPORT_FAILED>;
export type ProductsBulkImportRequestedAction =
	Action<BulkImportActionTypes.PRODUCTS_BULK_IMPORT_REQUESTED>;
export interface ProductsBulkImportSuccessfulAction
	extends Action<BulkImportActionTypes.PRODUCTS_BULK_IMPORT_SUCCESSFUL> {
	bulkImportData: BulkImportResponse<Product>;
}
export type ProductsBulkImportFailedAction =
	Action<BulkImportActionTypes.PRODUCTS_BULK_IMPORT_FAILED>;
export type ClientsBulkImportRequestedAction =
	Action<BulkImportActionTypes.CLIENTS_BULK_IMPORT_REQUESTED>;
export interface ClientsBulkImportSuccessfulAction
	extends Action<BulkImportActionTypes.CLIENTS_BULK_IMPORT_SUCCESSFUL> {
	bulkImportData: BulkImportResponse<Client>;
}
export type ClientsBulkImportFailedAction =
	Action<BulkImportActionTypes.CLIENTS_BULK_IMPORT_FAILED>;

export type ClientContactsBulkImportRequestedAction =
	Action<BulkImportActionTypes.CLIENT_CONTACTS_BULK_IMPORT_REQUESTED>;
export interface ClientContactsBulkImportSuccessfulAction
	extends Action<BulkImportActionTypes.CLIENT_CONTACTS_BULK_IMPORT_SUCCESSFUL> {
	bulkImportData: BulkImportResponse<ClientContact>;
}
export type ClientContactsBulkImportFailedAction =
	Action<BulkImportActionTypes.CLIENT_CONTACTS_BULK_IMPORT_FAILED>;
export type IssuesBulkImportRequestedAction =
	Action<BulkImportActionTypes.ISSUES_BULK_IMPORT_REQUESTED>;
export interface IssuesBulkImportSuccessfulAction
	extends Action<BulkImportActionTypes.ISSUES_BULK_IMPORT_SUCCESSFUL> {
	bulkImportData: BulkImportResponse<AssessmentDataLog | HealthStatusDataLog>;
}
export type IssuesBulkImportFailedAction =
	Action<BulkImportActionTypes.ISSUES_BULK_IMPORT_FAILED>;
export type ResetBulkImportAction =
	Action<BulkImportActionTypes.BULK_IMPORT_RESET>;
export type SpreadsheetBulkImportDrawerOpenedAction =
	Action<BulkImportActionTypes.SPREADSHEET_BULK_IMPORT_DRAWER_OPENED>;
export type SpreadsheetBulkImportDrawerClosedAction =
	Action<BulkImportActionTypes.SPREADSHEET_BULK_IMPORT_DRAWER_CLOSED>;
export type SoftwareBulkImportDrawerOpenedAction =
	Action<BulkImportActionTypes.SOFTWARE_BULK_IMPORT_DRAWER_OPENED>;
export type SoftwareBulkImportDrawerClosedAction =
	Action<BulkImportActionTypes.SOFTWARE_BULK_IMPORT_DRAWER_CLOSED>;
export type EmailBulkImportDrawerOpenedAction =
	Action<BulkImportActionTypes.EMAIL_BULK_IMPORT_DRAWER_OPENED>;
export type EmailBulkImportDrawerClosedAction =
	Action<BulkImportActionTypes.EMAIL_BULK_IMPORT_DRAWER_CLOSED>;
export type BulkImportEntityListDrawerOpenedAction =
	Action<BulkImportActionTypes.BULK_IMPORT_ENTITY_LIST_DRAWER_OPENED>;
export type BulkImportEntityListDrawerClosedAction =
	Action<BulkImportActionTypes.BULK_IMPORT_ENTITY_LIST_DRAWER_CLOSED>;
export interface RestoreBulkImportStateAction
	extends Action<BulkImportActionTypes.RESTORE_BULK_IMPORT_STATE> {
	bulkImportState: {
		spreadsheetBulkImportDrawerOpen?: boolean;
	};
}

export type BulkImportActions =
	| ShowBulkImportLoaderAction
	| HideBulkImportLoaderAction
	| OpportunitiesBulkImportRequestedAction
	| OpportunitiesBulkImportSuccessfulAction
	| OpportunitiesBulkImportFailedAction
	| ProductsBulkImportRequestedAction
	| ProductsBulkImportSuccessfulAction
	| ProductsBulkImportFailedAction
	| ClientsBulkImportRequestedAction
	| ClientsBulkImportSuccessfulAction
	| ClientsBulkImportFailedAction
	| ClientContactsBulkImportRequestedAction
	| ClientContactsBulkImportSuccessfulAction
	| ClientContactsBulkImportFailedAction
	| IssuesBulkImportRequestedAction
	| IssuesBulkImportSuccessfulAction
	| IssuesBulkImportFailedAction
	| ResetBulkImportAction
	| SpreadsheetBulkImportDrawerOpenedAction
	| SpreadsheetBulkImportDrawerClosedAction
	| SoftwareBulkImportDrawerOpenedAction
	| SoftwareBulkImportDrawerClosedAction
	| EmailBulkImportDrawerOpenedAction
	| EmailBulkImportDrawerClosedAction
	| BulkImportEntityListDrawerOpenedAction
	| BulkImportEntityListDrawerClosedAction
	| RestoreBulkImportStateAction;

export interface BulkImportDispatch {
	showBulkImportLoader: ActionCreator<
		ThunkAction<
			ShowBulkImportLoaderAction,
			GlobalState,
			null,
			ShowBulkImportLoaderAction
		>
	>;
	hideBulkImportLoader: ActionCreator<
		ThunkAction<
			HideBulkImportLoaderAction,
			GlobalState,
			null,
			HideBulkImportLoaderAction
		>
	>;
	bulkImportOpportunities: ActionCreator<
		ThunkAction<
			Promise<OpportunitiesBulkImportSuccessfulAction>,
			GlobalState,
			FormData,
			OpportunitiesBulkImportSuccessfulAction
		>
	>;
	bulkImportProducts: ActionCreator<
		ThunkAction<
			Promise<ProductsBulkImportSuccessfulAction>,
			GlobalState,
			FormData,
			ProductsBulkImportSuccessfulAction
		>
	>;
	bulkImportClients: ActionCreator<
		ThunkAction<
			Promise<ClientsBulkImportSuccessfulAction>,
			GlobalState,
			FormData,
			ClientsBulkImportSuccessfulAction
		>
	>;

	bulkImportClientContacts: ActionCreator<
		ThunkAction<
			Promise<ClientContactsBulkImportSuccessfulAction>,
			GlobalState,
			FormData,
			ClientContactsBulkImportSuccessfulAction
		>
	>;
	bulkImportAssessmentIssues: ActionCreator<
		ThunkAction<
			Promise<IssuesBulkImportSuccessfulAction>,
			GlobalState,
			{ formData: FormData; currentActiveAssessmentId: string },
			IssuesBulkImportSuccessfulAction
		>
	>;
	bulkImportQbrIssues: ActionCreator<
		ThunkAction<
			Promise<IssuesBulkImportSuccessfulAction>,
			GlobalState,
			{ formData: FormData; currentActiveQbrId: string },
			IssuesBulkImportSuccessfulAction
		>
	>;
	resetBulkImport: ActionCreator<
		ThunkAction<
			ResetBulkImportAction,
			GlobalState,
			null,
			ResetBulkImportAction
		>
	>;
	spreadsheetBulkImportDrawerOpened: ActionCreator<
		ThunkAction<
			SpreadsheetBulkImportDrawerOpenedAction,
			GlobalState,
			null,
			SpreadsheetBulkImportDrawerOpenedAction
		>
	>;
	spreadsheetBulkImportDrawerClosed: ActionCreator<
		ThunkAction<
			SpreadsheetBulkImportDrawerClosedAction,
			GlobalState,
			null,
			SpreadsheetBulkImportDrawerClosedAction
		>
	>;
	softwareBulkImportDrawerOpened: ActionCreator<
		ThunkAction<
			SoftwareBulkImportDrawerOpenedAction,
			GlobalState,
			null,
			SoftwareBulkImportDrawerOpenedAction
		>
	>;
	softwareBulkImportDrawerClosed: ActionCreator<
		ThunkAction<
			SoftwareBulkImportDrawerClosedAction,
			GlobalState,
			null,
			SoftwareBulkImportDrawerClosedAction
		>
	>;
	emailBulkImportDrawerOpened: ActionCreator<
		ThunkAction<
			EmailBulkImportDrawerOpenedAction,
			GlobalState,
			null,
			EmailBulkImportDrawerOpenedAction
		>
	>;
	emailBulkImportDrawerClosed: ActionCreator<
		ThunkAction<
			EmailBulkImportDrawerClosedAction,
			GlobalState,
			null,
			EmailBulkImportDrawerClosedAction
		>
	>;
	bulkImportEntityListDrawerOpened: ActionCreator<
		ThunkAction<
			BulkImportEntityListDrawerOpenedAction,
			GlobalState,
			null,
			BulkImportEntityListDrawerOpenedAction
		>
	>;
	bulkImportEntityListDrawerClosed: ActionCreator<
		ThunkAction<
			BulkImportEntityListDrawerClosedAction,
			GlobalState,
			null,
			BulkImportEntityListDrawerClosedAction
		>
	>;
	restoreBulkImportState: ActionCreator<
		ThunkAction<
			RestoreBulkImportStateAction,
			GlobalState,
			{ spreadsheetBulkImportDrawerOpen?: boolean },
			RestoreBulkImportStateAction
		>
	>;
}

export const BulkImportActionCreators: BulkImportDispatch = {
	showBulkImportLoader: () => {
		return (dispatch: Dispatch): ShowBulkImportLoaderAction => {
			const showBulkImportLoaderAction: ShowBulkImportLoaderAction = {
				type: BulkImportActionTypes.SHOW_BULK_IMPORT_LOADER
			};
			return dispatch(showBulkImportLoaderAction);
		};
	},
	hideBulkImportLoader: () => {
		return (dispatch: Dispatch): HideBulkImportLoaderAction => {
			const hideBulkImportLoaderAction: HideBulkImportLoaderAction = {
				type: BulkImportActionTypes.HIDE_BULK_IMPORT_LOADER
			};
			return dispatch(hideBulkImportLoaderAction);
		};
	},
	bulkImportOpportunities: (formData: FormData) => {
		return async (
			dispatch: Dispatch
		): Promise<OpportunitiesBulkImportSuccessfulAction> => {
			const opportunitiesBulkImportRequestedAction: OpportunitiesBulkImportRequestedAction =
				{
					type: BulkImportActionTypes.OPPORTUNITIES_BULK_IMPORT_REQUESTED
				};
			dispatch(opportunitiesBulkImportRequestedAction);
			try {
				const opportunitiesBulkImportResponse =
					await opportunitiesBulkImport(formData);
				const opportunitiesBulkImportSuccessfulAction: OpportunitiesBulkImportSuccessfulAction =
					{
						type: BulkImportActionTypes.OPPORTUNITIES_BULK_IMPORT_SUCCESSFUL,
						bulkImportData: opportunitiesBulkImportResponse
					};
				return dispatch(opportunitiesBulkImportSuccessfulAction);
			} catch (error) {
				const opportunitiesBulkImportFailedAction: OpportunitiesBulkImportFailedAction =
					{
						type: BulkImportActionTypes.OPPORTUNITIES_BULK_IMPORT_FAILED
					};
				dispatch(opportunitiesBulkImportFailedAction);

				return Promise.reject(error);
			}
		};
	},
	bulkImportProducts: (formData: FormData) => {
		return async (
			dispatch: Dispatch
		): Promise<ProductsBulkImportSuccessfulAction> => {
			const productsBulkImportRequestedAction: ProductsBulkImportRequestedAction =
				{
					type: BulkImportActionTypes.PRODUCTS_BULK_IMPORT_REQUESTED
				};
			dispatch(productsBulkImportRequestedAction);
			try {
				const productsBulkImportResponse = await productsBulkImport(
					formData
				);
				const productsBulkImportSuccessfulAction: ProductsBulkImportSuccessfulAction =
					{
						type: BulkImportActionTypes.PRODUCTS_BULK_IMPORT_SUCCESSFUL,
						bulkImportData: productsBulkImportResponse
					};
				return dispatch(productsBulkImportSuccessfulAction);
			} catch (error) {
				const productsBulkImportFailedAction: ProductsBulkImportFailedAction =
					{
						type: BulkImportActionTypes.PRODUCTS_BULK_IMPORT_FAILED
					};
				dispatch(productsBulkImportFailedAction);

				return Promise.reject(error);
			}
		};
	},
	bulkImportClients: (formData: FormData) => {
		return async (
			dispatch: Dispatch
		): Promise<ClientsBulkImportSuccessfulAction> => {
			const clientsBulkImportRequestedAction: ClientsBulkImportRequestedAction =
				{
					type: BulkImportActionTypes.CLIENTS_BULK_IMPORT_REQUESTED
				};
			dispatch(clientsBulkImportRequestedAction);
			try {
				const clientsBulkImportResponse = await clientsBulkImport(
					formData
				);
				const clientsBulkImportSuccessfulAction: ClientsBulkImportSuccessfulAction =
					{
						type: BulkImportActionTypes.CLIENTS_BULK_IMPORT_SUCCESSFUL,
						bulkImportData: clientsBulkImportResponse
					};
				return dispatch(clientsBulkImportSuccessfulAction);
			} catch (error) {
				const clientsBulkImportFailedAction: ClientsBulkImportFailedAction =
					{
						type: BulkImportActionTypes.CLIENTS_BULK_IMPORT_FAILED
					};
				dispatch(clientsBulkImportFailedAction);

				return Promise.reject(error);
			}
		};
	},
	bulkImportClientContacts: (formData: FormData) => {
		return async (
			dispatch: Dispatch
		): Promise<ClientContactsBulkImportSuccessfulAction> => {
			const clientContactsBulkImportRequestedAction: ClientContactsBulkImportRequestedAction =
				{
					type: BulkImportActionTypes.CLIENT_CONTACTS_BULK_IMPORT_REQUESTED
				};
			dispatch(clientContactsBulkImportRequestedAction);
			try {
				const clientContactsBulkImportResponse =
					await clientContactsBulkImport(formData);
				const clientContactsBulkImportSuccessfulAction: ClientContactsBulkImportSuccessfulAction =
					{
						type: BulkImportActionTypes.CLIENT_CONTACTS_BULK_IMPORT_SUCCESSFUL,
						bulkImportData: clientContactsBulkImportResponse
					};
				return dispatch(clientContactsBulkImportSuccessfulAction);
			} catch (error) {
				const clientContactsBulkImportFailedAction: ClientContactsBulkImportFailedAction =
					{
						type: BulkImportActionTypes.CLIENT_CONTACTS_BULK_IMPORT_FAILED
					};
				dispatch(clientContactsBulkImportFailedAction);

				return Promise.reject(error);
			}
		};
	},
	bulkImportAssessmentIssues: (
		formData: FormData,
		currentActiveAssessmentId: string
	) => {
		return async (
			dispatch: Dispatch
		): Promise<IssuesBulkImportSuccessfulAction> => {
			const assessmentIssuesBulkImportRequestedAction: IssuesBulkImportRequestedAction =
				{
					type: BulkImportActionTypes.ISSUES_BULK_IMPORT_REQUESTED
				};
			dispatch(assessmentIssuesBulkImportRequestedAction);
			try {
				const assessmentIssuesBulkImportResponse =
					await assessmentIssuesBulkImport(
						formData,
						currentActiveAssessmentId
					);
				const assessmentIssuesBulkImportSuccessfulAction: IssuesBulkImportSuccessfulAction =
					{
						type: BulkImportActionTypes.ISSUES_BULK_IMPORT_SUCCESSFUL,
						bulkImportData: assessmentIssuesBulkImportResponse
					};
				return dispatch(assessmentIssuesBulkImportSuccessfulAction);
			} catch (error) {
				const assessmentIssuesBulkImportFailedAction: IssuesBulkImportFailedAction =
					{
						type: BulkImportActionTypes.ISSUES_BULK_IMPORT_FAILED
					};
				dispatch(assessmentIssuesBulkImportFailedAction);
				return Promise.reject(error);
			}
		};
	},
	bulkImportQbrIssues: (formData: FormData, currentActiveQbrId: string) => {
		return async (
			dispatch: Dispatch
		): Promise<IssuesBulkImportSuccessfulAction> => {
			const qbrIssuesBulkImportRequestedAction: IssuesBulkImportRequestedAction =
				{
					type: BulkImportActionTypes.ISSUES_BULK_IMPORT_REQUESTED
				};
			dispatch(qbrIssuesBulkImportRequestedAction);
			try {
				const qbrIssuesBulkImportResponse = await qbrIssuesBulkImport(
					formData,
					currentActiveQbrId
				);
				const qbrIssuesBulkImportSuccessfulAction: IssuesBulkImportSuccessfulAction =
					{
						type: BulkImportActionTypes.ISSUES_BULK_IMPORT_SUCCESSFUL,
						bulkImportData: qbrIssuesBulkImportResponse
					};
				return dispatch(qbrIssuesBulkImportSuccessfulAction);
			} catch (error) {
				const qbrIssuesBulkImportFailedAction: IssuesBulkImportFailedAction =
					{
						type: BulkImportActionTypes.ISSUES_BULK_IMPORT_FAILED
					};
				dispatch(qbrIssuesBulkImportFailedAction);
				return Promise.reject(error);
			}
		};
	},
	resetBulkImport: () => {
		return (dispatch: Dispatch): ResetBulkImportAction => {
			const resetBulkImportAction: ResetBulkImportAction = {
				type: BulkImportActionTypes.BULK_IMPORT_RESET
			};
			return dispatch(resetBulkImportAction);
		};
	},
	spreadsheetBulkImportDrawerOpened: () => {
		return (
			dispatch: Dispatch
		): SpreadsheetBulkImportDrawerOpenedAction => {
			const spreadsheetBulkImportDrawerOpenedAction: SpreadsheetBulkImportDrawerOpenedAction =
				{
					type: BulkImportActionTypes.SPREADSHEET_BULK_IMPORT_DRAWER_OPENED
				};
			return dispatch(spreadsheetBulkImportDrawerOpenedAction);
		};
	},
	spreadsheetBulkImportDrawerClosed: () => {
		return (
			dispatch: Dispatch
		): SpreadsheetBulkImportDrawerClosedAction => {
			const spreadsheetBulkImportDrawerClosedAction: SpreadsheetBulkImportDrawerClosedAction =
				{
					type: BulkImportActionTypes.SPREADSHEET_BULK_IMPORT_DRAWER_CLOSED
				};
			return dispatch(spreadsheetBulkImportDrawerClosedAction);
		};
	},
	softwareBulkImportDrawerOpened: () => {
		return (dispatch: Dispatch): SoftwareBulkImportDrawerOpenedAction => {
			const softwareBulkImportDrawerOpenedAction: SoftwareBulkImportDrawerOpenedAction =
				{
					type: BulkImportActionTypes.SOFTWARE_BULK_IMPORT_DRAWER_OPENED
				};
			return dispatch(softwareBulkImportDrawerOpenedAction);
		};
	},
	softwareBulkImportDrawerClosed: () => {
		return (dispatch: Dispatch): SoftwareBulkImportDrawerClosedAction => {
			const softwareBulkImportDrawerClosedAction: SoftwareBulkImportDrawerClosedAction =
				{
					type: BulkImportActionTypes.SOFTWARE_BULK_IMPORT_DRAWER_CLOSED
				};
			return dispatch(softwareBulkImportDrawerClosedAction);
		};
	},
	emailBulkImportDrawerOpened: () => {
		return (dispatch: Dispatch): EmailBulkImportDrawerOpenedAction => {
			const emailBulkImportDrawerOpenedAction: EmailBulkImportDrawerOpenedAction =
				{
					type: BulkImportActionTypes.EMAIL_BULK_IMPORT_DRAWER_OPENED
				};
			return dispatch(emailBulkImportDrawerOpenedAction);
		};
	},
	emailBulkImportDrawerClosed: () => {
		return (dispatch: Dispatch): EmailBulkImportDrawerClosedAction => {
			const emailBulkImportDrawerClosedAction: EmailBulkImportDrawerClosedAction =
				{
					type: BulkImportActionTypes.EMAIL_BULK_IMPORT_DRAWER_CLOSED
				};
			return dispatch(emailBulkImportDrawerClosedAction);
		};
	},
	bulkImportEntityListDrawerOpened: () => {
		return (dispatch: Dispatch): BulkImportEntityListDrawerOpenedAction => {
			const bulkImportEntityListDrawerOpenedAction: BulkImportEntityListDrawerOpenedAction =
				{
					type: BulkImportActionTypes.BULK_IMPORT_ENTITY_LIST_DRAWER_OPENED
				};
			return dispatch(bulkImportEntityListDrawerOpenedAction);
		};
	},
	bulkImportEntityListDrawerClosed: () => {
		return (dispatch: Dispatch): BulkImportEntityListDrawerClosedAction => {
			const bulkImportEntityListDrawerClosedAction: BulkImportEntityListDrawerClosedAction =
				{
					type: BulkImportActionTypes.BULK_IMPORT_ENTITY_LIST_DRAWER_CLOSED
				};
			return dispatch(bulkImportEntityListDrawerClosedAction);
		};
	},
	restoreBulkImportState: (bulkImportState: { spreadsheetBulkImportDrawerOpen?: boolean }) => {
		return (dispatch: Dispatch): RestoreBulkImportStateAction => {
			const restoreBulkImportStateAction: RestoreBulkImportStateAction = {
				type: BulkImportActionTypes.RESTORE_BULK_IMPORT_STATE,
				bulkImportState
			};
			return dispatch(restoreBulkImportStateAction);
		};
	}
};

export default BulkImportActionCreators;
